import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import AccordionDetails from '@mui/material/AccordionDetails';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import type { CSSProperties, ReactNode } from 'react';

/**
 * Componente CardAccordion
 *
 * Acordeón estilizado para mostrar y ocultar contenido.
 *
 * @component
 * @param {object} props - Props del componente.
 * @param {string} props.id - ID único del acordeón.
 * @param {string} props.title - Título del acordeón.
 * @param {boolean} [props.canToggle] - Permite expandir/colapsar.
 * @param {boolean} [props.activexpanded] - Si está expandido por defecto.
 * @param {ReactNode} [props.titleChildren] - Elementos adicionales en el título.
 * @param {object} [props.styleAcccordion] - Estilos personalizados para el acordeón.
 * @param {object} [props.styleAcccordionDetail] - Estilos para el detalle.
 * @param {boolean} [props.hasAccordionSummary] - Muestra el resumen del acordeón.
 * @example
 * <CardAccordion id="acordeon1" title="Título">Contenido</CardAccordion>
 */

type Props = {
  id: string;
  title: string;
  canToggle?: boolean;
  activexpanded?: boolean;
  titleChildren?: ReactNode;
  styleAcccordion?: CSSProperties;
  styleAcccordionDetail?: CSSProperties;
  hasAccordionSummary?: boolean;
};

function CardAccordion({
  id,
  title,
  children,
  canToggle = true,
  activexpanded = true,
  titleChildren = null,
  styleAcccordion = {},
  styleAcccordionDetail = {},
  hasAccordionSummary = true
}: Props & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <Accordion
      defaultExpanded={activexpanded}
      sx={{
        ...styleAcccordion,
        padding: 2,
        background: 'rgba(245, 247, 251, 0.76)',
        borderRadius: '4px',
        margin: '10px'
      }}
    >
      {hasAccordionSummary && (
        <AccordionSummary
          aria-controls={`${id}-content`}
          expandIcon={canToggle ? <OpenInFullIcon /> : null}
          id={`${id}-header`}
          sx={{
            '.MuiAccordionSummary-content': {
              marginY: 2,
              color: '#0E335C',
              display: 'flex',
              alignItems: 'center',
              flexFlow: {
                xs: 'column',
                md: 'row'
              }
            }
          }}
        >
          <Typography fontSize={16} fontWeight={500} paddingBottom={1}>
            {title}
          </Typography>
          {titleChildren}
        </AccordionSummary>
      )}
      <AccordionDetails
        sx={{
          ...styleAcccordionDetail
        }}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

export default CardAccordion;
